import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/atoms/form';
import { PortalAuthLayout } from '@/components/portal/PortalAuthLayout';
import { usePortalLogin } from '@/hooks/portal/usePortalAuth';

export const Route = createFileRoute('/(portal)/portal/login')({
  component: PortalLoginPage,
});

const schema = z.object({
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});
type Values = z.infer<typeof schema>;

function PortalLoginPage() {
  const navigate = useNavigate();
  const login = usePortalLogin();
  const [accounts, setAccounts] = useState<Array<{ tenantId: string; name: string }> | null>(null);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  async function attempt(values: Values, tenantId?: string) {
    try {
      const result = await login.mutateAsync({ ...values, tenantId });
      if (result.kind === 'select') {
        setAccounts(result.accounts);
        return;
      }
      await navigate({ to: '/portal' });
    } catch {
      setAccounts(null);
      form.setError('password', { message: 'Invalid email or password.' });
    }
  }

  // Company picker: the same email + password matched more than one company.
  if (accounts) {
    return (
      <PortalAuthLayout
        title="Choose your company"
        description="Your email is set up with more than one company. Which one do you want to open?"
        footer={
          <button
            type="button"
            onClick={() => setAccounts(null)}
            className="font-medium text-foreground underline"
          >
            Back
          </button>
        }
      >
        <div className="space-y-2">
          {accounts.map((account) => (
            <Button
              key={account.tenantId}
              variant="outline"
              className="w-full justify-between"
              disabled={login.isPending}
              onClick={() => void attempt(form.getValues(), account.tenantId)}
            >
              {account.name}
              <ChevronRight className="size-4" />
            </Button>
          ))}
        </div>
      </PortalAuthLayout>
    );
  }

  return (
    <PortalAuthLayout
      title="Sign in"
      description="Access your invoices and payments."
      footer={
        <>
          New here?{' '}
          <Link to="/portal/register" className="font-medium text-foreground underline">
            Request access
          </Link>
        </>
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit((v) => attempt(v))} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>Password</FormLabel>
                  <Link to="/portal/forgot" className="text-xs text-muted-foreground underline">
                    Forgot password?
                  </Link>
                </div>
                <FormControl>
                  <Input type="password" autoComplete="current-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" className="w-full" disabled={login.isPending}>
            {login.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
            Sign in
          </Button>
        </form>
      </Form>
    </PortalAuthLayout>
  );
}
